import CustomCard from "components/Atoms/CustomCard"
import CustomButton from "components/Atoms/CustomButton"
import CustomSpinner from "components/Atoms/CustomSpinner"
import NoResults from "components/Organisms/NoResults"
import { useCurrentDepartmentAccess } from "graphql/auth/auth.hooks"
import {
  useBackendTripDetails,
  useFrontendTripDetails,
} from "graphql/trip/trip.hooks"
import useTripsSnapshot from "hooks/useTripsSnapshot"
import { pick, tail } from "lodash-es"
import React, { useCallback, useEffect } from "react"
import { useDispatch } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import {
  VEHICLE_CLOSE_FILTER,
  VEHICLE_CLOSE_SUMMARY,
} from "store/vehicle.redux"
import { isUuid } from "utils/custom.utils"
import TripTrackingMap from "../map/TripTrackingMap"
import AddSchedule from "./AddSchedule"
import Bookings from "./BookingList"
import useTripProgress from "./front-end/useTripProgressData"
import Recommendations from "./Recommendations"
import TripContext from "./TripContext"
import TripProgress from "./TripProgress"
import TripSummary from "./TripSummary"

export default function TripDetailsPage() {
  const navigate = useNavigate()
  const isEcom = useCurrentDepartmentAccess()

  /* Get Snapshot of a single trip, we do this here and not through route state because we want this
     Page to be independent of any other route (imagine a scenario where the user navigates directly to this page)
     in that case the route state will be null since the user lands directly on this page
  */
  const { uuid } = useParams()
  const dispatch = useDispatch()

  /**
   * Get the snapshot of the current trip
   * i.e only get the trip details ONCE
   * This is not fired again till the component re-renders
   */

  const { data: firebaseTrip } = useTripsSnapshot(uuid)

  // const { data: tripRecommendations } = useTripRecommendations();

  /**
   * get live location of vehicle
   * default this to Mohadi (this behaviour is not appropriate)
   */
  const liveLocation = {
    lat: parseFloat(firebaseTrip?.latitude),
    lng: parseFloat(firebaseTrip?.longitude),
  }
  const handleClick = useCallback(() => navigate(-1), [navigate])
  const [getBackendDetail, { data: backendData, loading: backLoading }] =
    useBackendTripDetails(liveLocation)
  const [getFrontendDetail, { data: frontendData, loading: frontLoading }] =
    useFrontendTripDetails()

  useEffect(() => {
    // Close any filter or summary since it is not required over here
    dispatch({
      type: VEHICLE_CLOSE_FILTER,
    })
    dispatch({
      type: VEHICLE_CLOSE_SUMMARY,
    })
  }, [dispatch])

  useEffect(() => {
    if (isEcom && uuid) {
      getFrontendDetail({ variables: { uuid } })
    } else {
      getBackendDetail()
    }
  }, [getBackendDetail, getFrontendDetail, isEcom, uuid])

  // const trip = tripDetailsMock;
  const trip = backendData?.tripDetails || frontendData?.tripDetails
  const bookings = trip?.optimisedBookings ?? trip?.bookings ?? trip?.orders
  const grouped = useTripProgress(bookings)
  if (backLoading || frontLoading) return <CustomSpinner />

  const source = trip?.sourceDepot
  const destination = trip?.destinationDepot
  const start = grouped?.SRT
  const startRoot = start?.[0]
  const startSub = tail(start)

  const end = grouped?.END
  const endRoot = end?.[0]
  const endSub = tail(end)

  const mid = grouped?.MID
  if (!isUuid(uuid))
    return (
      <section className="tp-trip-not-found">
        <NoResults
          icon="search"
          action={
            <CustomButton
              icon="arrow-left"
              text="Back"
              onClick={e => {
                navigate("/d/vehicle/assigned")
              }}
            />
          }
          title="Please select a valid trip"
        />
        ;
      </section>
    )

  return (
    <TripContext vehicle={trip?.vehicle} uuid={uuid} location={liveLocation}>
      <div className="vt-trip-tracking-page p-3">
        <CustomCard>
          <article>
            <CustomButton
              icon="arrow-left"
              minimal
              onClick={handleClick}
              className="vt-back-button"
            />
            Trip Details
          </article>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3 vt-column-1">
              <section>
                <TripSummary
                  tripSummary={pick(trip, [
                    "vehicle",
                    "aggregator",
                    "status",
                    "date",
                    "unloadedBy",
                  ])}
                />
              </section>
              <section>
                <Bookings bookings={bookings} />
              </section>
            </div>
            <div className="col-span-3 vt-column-1 vt-trip-progress-container">
              <TripProgress
                isEcom={isEcom}
                source={source}
                destination={destination}
                bookings={bookings}
                start={start}
                end={end}
                startRoot={startRoot}
                startSub={startSub}
                endRoot={endRoot}
                endSub={endSub}
                mid={mid}
              />
            </div>
            <div className="col-span-6">
              <TripTrackingMap
                source={source}
                destination={destination}
                firebaseTrip={firebaseTrip}
                bookings={bookings}
              />
              {isEcom ? null : (
                <>
                  <div className="flex items-end my-3">
                    <AddSchedule uuid={uuid} bookings={bookings} />
                  </div>
                  <Recommendations />
                </>
              )}
            </div>
          </div>
        </CustomCard>
      </div>
    </TripContext>
  )
}
